/*============================================================================
  cart-extras.js — Native upsell cart add-ons for Impulse
  Three independent, merchant-toggleable features driven off the existing
  Impulse cart drawer:
    1. Free-shipping progress bar  (live on cart change)
    2. Gift-milestone auto-add / auto-remove (no customer action)
    3. In-cart "Complete the look" complementary upsell (Recommendations API)

  Hooks the theme's own events:
    - `cart:updated`        (dispatched by theme.CartForm on qty change; detail.cart = full cart JSON)
    - `ajaxProduct:added`   (dispatched on add-to-cart; no cart JSON -> we refetch /cart.js)
    - `cart:build`          (fired by third-party apps e.g. Easy Bundle after their own add; we refetch)
  All config comes from window.CartExtras (injected by cart-drawer.liquid from
  theme settings), so Adam controls everything from the Theme Editor.
============================================================================*/
(function () {
  'use strict';

  var CFG = window.CartExtras || {};
  var MONEY_FORMAT = (window.theme && theme.settings && theme.settings.moneyFormat) || '${{amount}}';

  /* ---- money formatting: reuse theme.Currency if present ---- */
  function money(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, MONEY_FORMAT);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---- fetch full cart JSON (used when an event lacks it) ---- */
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        return r.json(); // rejects if the body isn't JSON (e.g. an HTML error page)
      });
  }

  /* ---- POST /cart/add.js ---- */
  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  }

  /* ---- POST /cart/change.js (used to remove the gift) ---- */
  function changeLine(id, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ---- POST /cart/update.js by variant id ----
     Removes/sets a line by VARIANT id without first needing a /cart.js read to
     resolve the line key. Used for Redo removal so the opt-out + stale cleanup
     work even when an early /cart.js GET is transiently blocked (storefront
     web-pixel). No-ops server-side if the variant isn't in the cart. */
  function updateByVariant(variantId, quantity) {
    var updates = {};
    updates[variantId] = quantity;
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  /* Tell the theme to repaint the drawer + refire cart:updated with fresh JSON. */
  function rebuildDrawer(cart) {
    if (window.theme && theme.cart && document.querySelector('#CartDrawerForm')) {
      ownBuild = true; // our cart:updated below carries the JSON; skip the foreign-build refetch
      try { document.dispatchEvent(new CustomEvent('cart:build')); } finally { ownBuild = false; }
    }
    if (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    }
  }

  /* ---- eligible subtotal: cart total minus every excluded line ----
     The discount/free-shipping tiers count MERCHANDISE only. Non-merchandise
     lines (Redo return-protection fee, gift card, the free Mystery Shirt gift
     itself) are tagged `discount-exclude` server-side and their product ids are
     emitted into CFG.excludedProductIds by cart-drawer.liquid. Subtract each such
     line's final_line_price from cart.total_price. Because the Mystery Shirt gift
     line is itself `discount-exclude`, this ALSO nets out the auto-gift line — so
     the gift-qualifying math must NOT subtract the gift line a second time.
     Types are coerced (ids may be strings vs numbers); result is clamped >= 0. */
  function excludedIdSet() {
    var ids = (CFG && CFG.excludedProductIds) || [];
    var set = {};
    for (var i = 0; i < ids.length; i++) set[String(ids[i])] = true;
    return set;
  }
  /* Hobolax 10-02 QA: Shopify checks the $100 order-discount minimum and the $125 BXGY "buy"
     amount BEFORE order-level discounts (items_subtotal_price), but price-based shipping rates
     AFTER them (total_price). Counting the post-10% total made a $102.75 cart read "$7.52 to go"
     for a 10% that was already applied, and stripped the Mystery Shirt from $125-$138.88 carts.
     basis 'pre'  = items_subtotal_price (discount tiers + gift);  'post' = total_price (shipping). */
  function eligibleSubtotal(cart, basis, includeTeam) {
    if (!cart || typeof cart.total_price !== 'number') return 0;
    var pre = basis === 'pre' && typeof cart.items_subtotal_price === 'number';
    var total = pre ? cart.items_subtotal_price : cart.total_price;
    if (!Array.isArray(cart.items)) return Math.max(0, total);
    var excl = excludedIdSet();
    var excluded = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      // The US Redo product is UNLISTED, so it never reaches the storefront exclude
      // collection (live list = gift card only) even though it's tagged and the Liquid
      // bar nets it out. Match the Redo line directly so JS and Liquid agree.
      var isRedo = it && it.properties && (it.properties._redo_protection === 'true' || it.properties._redo_protection === true);
      // The auto-added gift never counts toward a tier (it is $0 while earned, but is briefly
      // charged when the BXGY lapses, or outside USD) - so it can't self-qualify the bar.
      var isGift = it && it.properties && (it.properties._free_gift === 'true' || it.properties._free_gift === true);
      // Rob 10-22: team-store gear never counts toward a reward (Shopify's 10%/mystery-shirt scopes skip it via
      // discount-exclude). It's tagged discount-exclude too, but team products are UNLISTED so they never reach
      // the storefront exclude collection either - match the vendor directly, or the bar auto-adds a Mystery
      // Shirt that Shopify then charges for (team $59.90 + Hobo $89.85 cart, tested 10-22).
      // includeTeam = the team-mode free-shipping basis (whole cart, team gear counts; see teamShip()).
      var isTeam = isTeamLine(it);
      if (it && ((excl[String(it.product_id)] && !(includeTeam && isTeam)) || isRedo || isGift || (isTeam && !includeTeam))) {
        // Subtract the line's contribution to cart.total_price. total_price is the
        // sum of each line's `line_price` (cart-level/order discount ALLOCATED),
        // which can differ from final_line_price (line-level discounts only). Using
        // line_price nets the line out EXACTLY — so e.g. the 10% Tier-2 order
        // discount that Shopify allocates onto the Redo line doesn't leak back into
        // the eligible subtotal. Fall back to final_line_price if line_price absent.
        var contrib = pre ? (it.final_line_price || 0)
          : ((typeof it.line_price === 'number') ? it.line_price : (it.final_line_price || 0));
        excluded += contrib;
      }
    }
    return Math.max(0, total - excluded);
  }

  /*==========================================================================
    1. FREE-SHIPPING PROGRESS BAR
  ==========================================================================*/
  // FIXED marker layout. Tier 1 sits a SHORT leading segment in from the left
  // (LEAD%, ~half the old 1/n origin), and the tiers are then spaced EVENLY from
  // there to the right edge, so the highest tier lands at 100%. Keeps a leading
  // segment from the START origin (0%) so a below-tier-1 cart still shows fill.
  var LEAD = 16.5; // % — leading segment length (start origin -> tier 1)
  // Rob 2026-09-24: with only TWO tiers live (tier 2 "10% off $100" was retired for
  // the Fall Bundle) a 16.5% lead left one marker stranded near the left edge and a
  // dead span across the whole middle. Widen the lead for the 2-tier case so the two
  // markers sit evenly. 3+ tiers keep the original 16.5 so nothing shifts if tier 2
  // ever comes back. ⛔ Must stay in lockstep with cart-shipping-bar.liquid, which
  // renders the FIRST paint — if only one is changed the markers jump on cart update.
  function leadFor(n) { return n === 2 ? 50 : LEAD; }
  function tierPos(i, n) {
    if (n <= 0) return 100;
    if (n === 1) return 100;
    var lead = leadFor(n);
    return lead + (i / (n - 1)) * (100 - lead);
  }

  // Piecewise fill: reaching a tier's $ threshold fills exactly to that tier's
  // marker position, interpolating linearly between thresholds (NOT by raw dollar
  // proportion). Below the first tier we scale from the START origin (0%) into the
  // first marker's position, so a below-tier-1 cart shows partial leading fill.
  function tierFill(subtotal, amounts) {
    var n = amounts.length;
    if (!n) return 0;
    if (subtotal >= amounts[n - 1]) return 100;
    if (subtotal <= 0) return 0;
    if (subtotal < amounts[0]) {
      // Leading segment: START (0%) up to the first marker (tierPos(0,n)).
      return amounts[0] > 0 ? (subtotal / amounts[0]) * tierPos(0, n) : 0;
    }
    for (var i = 0; i < n - 1; i++) {
      if (subtotal >= amounts[i] && subtotal < amounts[i + 1]) {
        var span = amounts[i + 1] - amounts[i];
        var frac = span > 0 ? (subtotal - amounts[i]) / span : 0;
        return tierPos(i, n) + frac * (tierPos(i + 1, n) - tierPos(i, n));
      }
    }
    return 100;
  }

  /* TEAM MODE (Rob 10-21/22). Whether a cart is a "team cart" is decided by what's IN it, exactly like checkout:
     any line from a team store (vendor "TS - ") -> free shipping comes at $100 on the WHOLE cart (team gear counts;
     native rate while a team store is open, data/team-stores/stores.json native_free_ship), while 10% Off! and the
     Mystery Shirt still count Hobo Lax gear only. No team gear -> the normal $75 bar, untouched. A team-page visitor
     buying only Hobo gear gets the normal bar, which matches checkout (free from $75). */
  var TEAM_SHIP_CENTS = 10000;
  function teamShip(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.some(isTeamLine)) return null;
    return { amount: TEAM_SHIP_CENTS, sub: eligibleSubtotal(cart, 'post', true) };
  }

  function updateShippingBar(cart) {
    var bar = document.querySelector('[data-tier-bar]');
    if (!bar) return;

    var amounts = (bar.getAttribute('data-tiers') || '')
      .split('|').filter(Boolean).map(function (s) { return parseInt(s, 10); });
    var labels = (bar.getAttribute('data-tier-labels') || '').split('|');
    var descs = (bar.getAttribute('data-tier-descs') || '').split('|');
    var types = (bar.getAttribute('data-tier-types') || '').split('|');
    if (!amounts.length) return;

    // Per-tier basis: shipping tiers vs the post-discount total, discount/gift tiers vs the
    // pre-order-discount subtotal (what Shopify itself evaluates). Mirrors cart-shipping-bar.liquid.
    var subPost = eligibleSubtotal(cart, 'post');
    var subPre = eligibleSubtotal(cart, 'pre');
    var team = teamShip(cart);
    if (team) {
      for (var ts = 0; ts < types.length; ts++) {
        if (types[ts] === 'shipping') amounts[ts] = Math.max(amounts[ts], team.amount);
      }
    }
    var subShip = team ? team.sub : subPost;
    function subFor(k) { return types[k] === 'shipping' ? subShip : subPre; }
    var note = bar.querySelector('[data-team-note]');
    if (team && !note) {
      note = document.createElement('p');
      note.setAttribute('data-team-note', '');
      note.style.cssText = 'margin:8px 0 0;font-size:11px;line-height:1.35;text-align:center;opacity:.75';
      note.textContent = 'Team store order: free US shipping over $100 (everything counts). 10% Off! and the Mystery Shirt count Hobo Lax gear only.';
      bar.appendChild(note);
    }
    if (note) note.hidden = !team;

    // Next unmet tier = lowest tier whose own basis is still below its amount.
    var nextAmount = null, nextLabel = '', nextIndex = -1;
    for (var j = 0; j < amounts.length; j++) {
      if (amounts[j] > subFor(j)) { nextAmount = amounts[j]; nextLabel = labels[j] || ''; nextIndex = j; break; }
    }
    // Fill/gap use the basis of the tier being chased (pre basis once everything is unlocked).
    var subtotal = nextIndex >= 0 ? subFor(nextIndex) : subPre;

    var pct = tierFill(subtotal, amounts);
    pct = Math.max(0, Math.min(100, pct));
    var fill = bar.querySelector('[data-shipping-bar-fill]');
    if (fill) fill.style.width = pct + '%';

    // Green (achieved) overlay = position of the last passed marker, so the bar
    // reads green for what's earned and cyan for the active stretch to NEXT.
    var wonPct = 0;
    for (var w = 0; w < amounts.length; w++) {
      if (subFor(w) >= amounts[w]) wonPct = tierPos(w, amounts.length);
    }
    if (nextIndex === -1) wonPct = 100;
    var fillWon = bar.querySelector('[data-shipping-bar-fill-won]');
    if (fillWon) fillWon.style.width = wonPct + '%';


    // Three-state markers: ACHIEVED (green ✓) / NEXT (amber, the only pulsing
    // one) / UPCOMING (grey 🔒). Pair every state with an icon, never color
    // alone. Detect a fresh unlock so we can fire the celebratory animation.
    var markers = bar.querySelectorAll('[data-tier-marker]');
    for (var i = 0; i < markers.length; i++) {
      var ma = (typeof amounts[i] === 'number') ? amounts[i] : (parseInt(markers[i].getAttribute('data-tier-amount'), 10) || 0); // team mode moves the shipping amount
      var achieved = subFor(i) >= ma;
      var wasAchieved = markers[i].classList.contains('is-achieved');
      var state = achieved ? 'achieved' : (i === nextIndex ? 'next' : 'upcoming');
      markers[i].classList.toggle('is-achieved', state === 'achieved');
      markers[i].classList.toggle('is-next', state === 'next');
      markers[i].classList.toggle('is-upcoming', state === 'upcoming');
      markers[i].style.left = tierPos(i, markers.length) + '%';
      // Just unlocked this sync -> play the unlock animation once.
      if (achieved && !wasAchieved) {
        markers[i].classList.remove('just-unlocked');
        // force reflow so the animation re-triggers
        void markers[i].offsetWidth; // eslint-disable-line no-unused-expressions
        markers[i].classList.add('just-unlocked');
      }
    }

    var msg = bar.querySelector('[data-shipping-bar-msg]');
    var almostCents = parseInt(bar.getAttribute('data-almost-cents'), 10);
    if (!isFinite(almostCents) || almostCents <= 0) almostCents = 1500;

    if (nextAmount === null) {
      var wasComplete = bar.classList.contains('is-complete');
      bar.classList.add('is-complete');
      if (!wasComplete) {
        bar.classList.remove('just-completed');
        void bar.offsetWidth; // eslint-disable-line no-unused-expressions
        bar.classList.add('just-completed');
      }
      // Top tier reached: nothing left to chase -> celebratory complete message.
      var completeTmpl = bar.getAttribute('data-msg-complete')
        || "You've unlocked it all![sub]Enjoy your rewards 🎉";
      // No [amount] in the complete copy -> render main + sub with no $ token.
      if (msg) setMsg(msg, completeTmpl, null);
    } else {
      bar.classList.remove('is-complete');
      if (msg) {
        var gap = nextAmount - subtotal;
        var almost = gap <= almostCents;
        var tmpl = almost
          ? (bar.getAttribute('data-msg-almost') || "You're [amount] Away![sub]You're almost there…")
          : (bar.getAttribute('data-msg-progress') || "You're [amount] Away![sub]You're almost there…");
        // The chained-to reward = the tier AFTER next (empty on the top tier).
        var chainLabel = (nextIndex >= 0 && nextIndex + 1 < labels.length) ? (labels[nextIndex + 1] || '') : '';
        var nextDesc = (nextIndex >= 0 && nextIndex < descs.length) ? (descs[nextIndex] || '') : '';
        var resolved = tmpl
          .replace('[reward]', nextLabel)
          .replace('[next_reward]', chainLabel)
          .replace('[desc]', nextDesc);
        // Render as a MAIN line + own-line SUB-TITLE, wrapping [amount] in <strong>
        // via DOM nodes (no innerHTML) so the gap $ is the most prominent element
        // while keeping the rest XSS-safe textContent.
        setMsg(msg, resolved, money(gap));
      }
    }
  }

  // Render a resolved template as a MAIN line (.csb-msg__main) plus an optional
  // own-line SUB-TITLE (.csb-msg__sub), split on the [sub] token. The [amount]
  // token (only ever on the main line) is wrapped in a <strong> so the gap $ is
  // the most prominent element. Everything is built from text nodes / created
  // elements — never innerHTML — so arbitrary label/template text can't inject
  // markup. amountStr may be null for templates with no [amount] (e.g. complete).
  function setMsg(el, resolved, amountStr) {
    while (el.firstChild) el.removeChild(el.firstChild);
    var subParts = resolved.split('[sub]');
    var mainText = subParts[0];
    var subText = subParts.length > 1 ? subParts.slice(1).join('[sub]') : '';

    var main = document.createElement('span');
    main.className = 'csb-msg__main';
    appendWithAmount(main, mainText, amountStr);
    el.appendChild(main);

    if (subText) {
      var sub = document.createElement('span');
      sub.className = 'csb-msg__sub';
      appendWithAmount(sub, subText, amountStr);
      el.appendChild(sub);
    }
  }

  // Append `text` to `el`, splitting on the [amount] token and inserting a
  // <strong> for the dollar gap (skipped when amountStr is null).
  function appendWithAmount(el, text, amountStr) {
    if (amountStr == null) { el.appendChild(document.createTextNode(text)); return; }
    var parts = text.split('[amount]');
    for (var k = 0; k < parts.length; k++) {
      if (parts[k]) el.appendChild(document.createTextNode(parts[k]));
      if (k < parts.length - 1) {
        var strong = document.createElement('strong');
        strong.textContent = amountStr;
        el.appendChild(strong);
      }
    }
  }

  /*==========================================================================
    2. GIFT-MILESTONE AUTO-ADD / AUTO-REMOVE
    Subtotal >= gift threshold  -> auto-add the configured gift variant (qty 1).
    Subtotal <  gift threshold  -> auto-remove it.
    Guarded by a flag so concurrent cart events don't double-add.
  ==========================================================================*/
  var giftBusy = false;
  var lastGiftPresent = false; // tracks whether the gift line was in the cart last sync
  var GIFT_DISMISS_KEY = 'cartExtrasGiftDismissed';

  // Gift threshold in cents. cart-drawer.liquid already emits this as integer
  // cents (rounded), so we DON'T multiply here. Guard against blank/garbage:
  // a non-finite or <= 0 threshold means "no valid gift milestone".
  function giftThresholdCents() {
    var t = CFG.gift && CFG.gift.threshold;
    t = (typeof t === 'number') ? t : parseInt(t, 10);
    return (isFinite(t) && t > 0) ? t : null;
  }

  // The gift line is the one matching the gift variant AND tagged _free_gift —
  // so a shopper who legitimately BUYS the same product isn't conflated with the
  // auto-gift (and we never delete their paid line on auto-remove).
  function findGiftLine(cart) {
    if (!CFG.gift || !cart || !Array.isArray(cart.items)) return null;
    var pid = CFG.gift.productId ? parseInt(CFG.gift.productId, 10) : null;
    var vid = CFG.gift.variantId ? parseInt(CFG.gift.variantId, 10) : null;
    if (!pid && !vid) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var tagged = it.properties && (it.properties._free_gift === 'true' || it.properties._free_gift === true);
      if (!tagged) continue;
      // Match by PRODUCT so any chosen size of the gift product is recognised as
      // the gift (the in-cart size selector swaps the variant). Fall back to the
      // legacy single-variant match only if no productId was emitted.
      if (pid) { if (it.product_id === pid) return it; }
      else if (it.variant_id === vid) return it;
    }
    return null;
  }

  // Dismiss key is the gift PRODUCT (fallback variant) so changing the gift's size
  // doesn't read as a fresh product and re-trigger the dismissal logic.
  function giftKey() { return String((CFG.gift && (CFG.gift.productId || CFG.gift.variantId)) || ''); }
  function giftDismissed() {
    try { return sessionStorage.getItem(GIFT_DISMISS_KEY) === giftKey(); }
    catch (e) { return false; }
  }
  function setGiftDismissed(on) {
    try {
      if (on) sessionStorage.setItem(GIFT_DISMISS_KEY, giftKey());
      else sessionStorage.removeItem(GIFT_DISMISS_KEY);
    } catch (e) { /* private mode — best effort */ }
  }

  // Race a promise against a timeout so a hung fetch can't strand giftBusy.
  function withTimeout(p, ms) {
    return Promise.race([
      p,
      new Promise(function (_, reject) { setTimeout(function () { reject(new Error('timeout')); }, ms || 8000); })
    ]);
  }

  function syncGift(cart) {
    if (!CFG.gift || !CFG.gift.enabled || !CFG.gift.variantId) return Promise.resolve(cart);
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve(cart);

    var threshold = giftThresholdCents();
    if (threshold === null) return Promise.resolve(cart); // no valid milestone -> do nothing

    var giftLine = findGiftLine(cart);

    // Qualifying subtotal = MERCHANDISE only. eligibleSubtotal() already nets out
    // every `discount-exclude` line — and the Mystery Shirt gift product is itself
    // tagged `discount-exclude`, so the auto-added gift line is already excluded
    // here. We therefore DON'T subtract the gift line again (that would double-
    // count a priced placeholder gift and could yo-yo the auto-add). Falls back to
    // the old gift-line subtraction only if the gift product isn't in the exclude
    // set (e.g. collection missing), so a $0/priced gift still can't game the line.
    var qualifying = eligibleSubtotal(cart, 'pre'); // already nets out the _free_gift line
    var qualifies = qualifying >= threshold;

    // Detect a USER-initiated removal: the gift was present last sync, is gone
    // now, we're not mid-operation, and the cart still qualifies -> the shopper
    // removed it deliberately. Remember that and DON'T fight them by re-adding.
    if (!giftBusy && lastGiftPresent && !giftLine && qualifies) {
      setGiftDismissed(true);
    }
    // Dismissal clears once the cart drops below threshold (so the gift can
    // re-unlock fresh next time it qualifies).
    if (!qualifies) setGiftDismissed(false);

    if (qualifies && !giftLine && giftDismissed()) {
      lastGiftPresent = false;
      setGiftUnlockedState(false);
      return Promise.resolve(cart);
    }

    lastGiftPresent = !!giftLine;

    if (giftBusy) return Promise.resolve(cart);

    if (qualifies && !giftLine) {
      giftBusy = true; // set SYNCHRONOUSLY before any await so concurrent events can't double-add
      var props = { _free_gift: 'true' }; // tag so theme/merchant can identify it
      return withTimeout(addToCart([{ id: parseInt(CFG.gift.variantId, 10), quantity: 1, properties: props }]))
        .then(function () { return fetchCart(); })
        .then(function (fresh) { giftBusy = false; lastGiftPresent = true; rebuildDrawer(fresh); setGiftUnlockedState(true); return fresh; })
        .catch(function (e) { giftBusy = false; console.warn('[cart-extras] gift add failed', e); return cart; });
    }

    if (!qualifies && giftLine) {
      giftBusy = true;
      return withTimeout(changeLine(giftLine.key, 0))
        .then(function (fresh) { giftBusy = false; lastGiftPresent = false; rebuildDrawer(fresh); setGiftUnlockedState(false); return fresh; })
        .catch(function (e) { giftBusy = false; console.warn('[cart-extras] gift remove failed', e); return cart; });
    }

    setGiftUnlockedState(!!giftLine);
    return Promise.resolve(cart);
  }

  function setGiftUnlockedState(unlocked) {
    var el = document.querySelector('[data-gift-status]');
    if (!el) return;
    el.classList.toggle('is-unlocked', !!unlocked);
    el.hidden = !unlocked;
  }

  /*==========================================================================
    2b. ADDITIONAL PRODUCT-REWARD TIERS (auto-add / auto-remove)
    The PRIMARY product reward (lowest-amount product tier) is handled by
    syncGift above, keeping its size-swap + checkout size-gate. Any FURTHER
    product tiers are simple: cross the threshold -> add qty 1 (tagged
    _free_gift), drop below -> remove. No size UI, no dismiss memory.
    Also the master-off cleanup: when the whole rewards system is disabled
    (CFG.enabled === false), every auto-added free line is removed so a
    mid-session toggle can't leave a free product stranded in a cart.
  ==========================================================================*/
  var extrasBusy = false;

  // Qualifying subtotal for extras = merchandise only: eligibleSubtotal() nets out
  // every discount-exclude line; we additionally net out any _free_gift line not
  // already in that set, so an auto-added free product can never inflate a
  // threshold and self-qualify the next tier.
  function extrasQualifying(cart) {
    return eligibleSubtotal(cart, 'pre'); // nets out discount-exclude, Redo and _free_gift lines
  }

  function findFreeLineByProduct(cart, pid) {
    pid = parseInt(pid, 10);
    if (!pid || !cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var tagged = it.properties && (it.properties._free_gift === 'true' || it.properties._free_gift === true);
      if (tagged && it.product_id === pid) return it;
    }
    return null;
  }

  // Remove EVERY auto-added free line (used when the master switch is off). Removes
  // one per pass; the follow-on cart:updated re-runs until none remain.
  function removeAllFreeLines(cart) {
    if (extrasBusy || giftBusy) return Promise.resolve(cart);
    var line = (cart.items || []).find(function (it) {
      return it.properties && (it.properties._free_gift === 'true' || it.properties._free_gift === true);
    });
    if (!line) return Promise.resolve(cart);
    extrasBusy = true;
    return withTimeout(changeLine(line.key, 0))
      .then(function (fresh) { extrasBusy = false; rebuildDrawer(fresh); return fresh; })
      .catch(function (e) { extrasBusy = false; console.warn('[cart-extras] free-line cleanup failed', e); return cart; });
  }

  function syncRewardExtras(cart) {
    var extras = (CFG && CFG.rewardExtras) || [];
    if (!extras.length) return Promise.resolve(cart);
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve(cart);
    if (extrasBusy || giftBusy) return Promise.resolve(cart);

    var qualifying = extrasQualifying(cart);

    // Act on the FIRST mismatch this pass; the resulting cart:updated re-syncs the rest.
    for (var i = 0; i < extras.length; i++) {
      var ex = extras[i];
      var threshold = parseInt(ex.threshold, 10);
      if (!isFinite(threshold) || threshold <= 0 || !ex.variantId) continue;
      var line = findFreeLineByProduct(cart, ex.productId);
      var qualifies = qualifying >= threshold;

      if (qualifies && !line) {
        extrasBusy = true;
        return withTimeout(addToCart([{ id: parseInt(ex.variantId, 10), quantity: 1, properties: { _free_gift: 'true' } }]))
          .then(function () { return fetchCart(); })
          .then(function (fresh) { extrasBusy = false; rebuildDrawer(fresh); return fresh; })
          .catch(function (e) { extrasBusy = false; console.warn('[cart-extras] extra reward add failed', e); return cart; });
      }
      if (!qualifies && line) {
        extrasBusy = true;
        return withTimeout(changeLine(line.key, 0))
          .then(function (fresh) { extrasBusy = false; rebuildDrawer(fresh); return fresh; })
          .catch(function (e) { extrasBusy = false; console.warn('[cart-extras] extra reward remove failed', e); return cart; });
      }
    }
    return Promise.resolve(cart);
  }

  /*==========================================================================
    3. IN-CART COMPLEMENTARY UPSELL
    Re-points at the cart's lead item on each change and (re)fetches the
    Recommendations API: intent=complementary, fallback intent=related.
    Manual-override tiles are server-rendered; we skip the fetch for them.
  ==========================================================================*/
  function buildTile(p) {
    var variants = p.variants || [];
    var first = variants[0] || {};
    var multi = variants.length > 1;
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var imgSrc = img ? escapeHtml(String(img).replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')) : '';
    var imgTag = imgSrc ? '<img src="' + imgSrc + '" width="80" height="80" loading="lazy" alt="">' : '';
    var url = escapeHtml(p.url || '#');

    var actions = '';
    if (multi) {
      actions += '<select class="cart-upsell__variant" data-upsell-variant aria-label="' + escapeHtml(p.title) + ' options">';
      variants.forEach(function (v) {
        actions += '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + '</option>';
      });
      actions += '</select>';
    }
    actions += '<button type="button" class="cart-upsell__add btn" data-upsell-add' +
      (multi ? '' : ' data-variant-id="' + first.id + '"') + '>' +
      escapeHtml(CFG.upsell.buttonText || 'Add') + '</button>';

    return '' +
      '<div class="cart-upsell__tile" data-upsell-tile>' +
        '<a href="' + url + '" class="cart-upsell__img" tabindex="-1" aria-hidden="true">' + imgTag + '</a>' +
        '<div class="cart-upsell__info">' +
          '<a href="' + url + '" class="cart-upsell__name">' + escapeHtml(p.title) + '</a>' +
          '<span class="cart-upsell__price">' + money(p.price) + '</span>' +
          '<div class="cart-upsell__actions">' + actions + '</div>' +
        '</div>' +
      '</div>';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function loadUpsell(cart) {
    var wrap = document.querySelector('[data-cart-upsell]');
    if (!wrap) return;
    if (wrap.getAttribute('data-manual') === 'true') return; // server-rendered, nothing to do

    var grid = wrap.querySelector('[data-cart-upsell-grid]');
    if (!grid) return;

    if (!cart.items.length) { grid.innerHTML = ''; grid.setAttribute('data-loaded-for', ''); return; }

    // Hobolax 10-18 (Rob, topic 70): under the $75 / $100 tiers the row becomes a
    // gap-closer ("Add one -> FREE shipping"); hbx-gap-closer.js owns it then.
    if (window.HbxGap) {
      var gapOwned = false;
      try {
        gapOwned = window.HbxGap.handle(cart, wrap, {
          eligibleSubtotal: eligibleSubtotal, excluded: excludedIdSet, money: money, teamShip: teamShip,
          giftProductId: function () { return CFG.gift && CFG.gift.productId ? parseInt(CFG.gift.productId, 10) : null; },
          addToCart: addToCart, fetchCart: fetchCart, rebuildDrawer: rebuildDrawer
        });
      } catch (e) { console.warn('[cart-extras] gap-closer failed', e); }
      if (gapOwned) return;
    }

    // Lead item = first cart line that is NOT the auto-added gift (match by product
    // so any chosen gift size is still excluded; fall back to the variant match).
    var giftPid = CFG.gift && CFG.gift.productId ? parseInt(CFG.gift.productId, 10) : null;
    var giftVid = CFG.gift && CFG.gift.variantId ? parseInt(CFG.gift.variantId, 10) : null;
    var lead = cart.items.find(function (it) {
      if (giftPid) return it.product_id !== giftPid;
      return !(giftVid && it.variant_id === giftVid);
    }) || cart.items[0];
    var productId = lead.product_id;

    var cartProductIds = cart.items.map(function (it) { return it.product_id; });

    // Cache signature = lead product + the full set of cart product ids. Keying on
    // BOTH means adding/removing ANY line (e.g. adding an upsell tile, so it must
    // now be filtered out) invalidates the cache and re-filters, while an unrelated
    // re-render with identical contents skips the network call.
    var sig = String(productId) + '|' + cartProductIds.slice().sort(function (a, b) { return a - b; }).join(',');
    if (grid.getAttribute('data-loaded-for') === sig) return; // already current
    grid.setAttribute('data-loaded-for', sig);

    // Recommendations API returns JSON when we request products.json
    var base = grid.getAttribute('data-recommendations-base') || '/recommendations/products';
    var limit = parseInt(wrap.getAttribute('data-limit'), 10) || 3;

    function tryIntent(intent) {
      var url = base + '.json?product_id=' + productId + '&limit=' + (limit + cartProductIds.length) + '&intent=' + intent;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) { return (data && data.products) || []; });
    }

    tryIntent('complementary')
      .then(function (products) {
        if (products.length) return products;
        return tryIntent('related');
      })
      .then(function (products) {
        // Filter out anything already in the cart OR flagged `discount-exclude`
        // (Redo return-protection, gift card, the mystery-shirt gift). Adam 2026-06-29:
        // most shoppers carry the $1.98 Redo line, so the recommendations API kept
        // surfacing it as "complementary" everywhere — it should never be upsold.
        var excl = excludedIdSet();
        var filtered = products.filter(function (p) {
          return cartProductIds.indexOf(p.id) === -1 && !excl[String(p.id)];
        }).slice(0, limit);
        if (!filtered.length) { grid.innerHTML = ''; wrap.classList.add('is-empty'); return; }
        wrap.classList.remove('is-empty');
        grid.innerHTML = filtered.map(buildTile).join('');
      })
      .catch(function (e) {
        grid.setAttribute('data-loaded-for', ''); // allow retry
        console.warn('[cart-extras] upsell load failed', e);
      });
  }

  /* One-tap add from an upsell tile (event-delegated so it survives re-renders). */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    var tile = btn.closest('[data-upsell-tile]');
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId && tile) {
      var sel = tile.querySelector('[data-upsell-variant]');
      if (sel) variantId = sel.value;
    }
    if (!variantId) return;
    btn.classList.add('is-loading');
    btn.disabled = true;
    addToCart([{ id: parseInt(variantId, 10), quantity: 1 }])
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        rebuildDrawer(cart);
        if (!document.documentElement.classList.contains('js-drawer-open')) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        console.warn('[cart-extras] upsell add failed', err);
      });
  });

  /* Gift size change: swap the free Mystery Shirt to the chosen size. The swap is a
     remove(old)+add(new) over the network, so it is ASYNC — the <select> updates
     instantly but the cart takes a moment. To stop a shopper from reaching checkout
     with the previous (e.g. default YS) size, we (a) DISABLE the checkout buttons for
     the duration of the swap, (b) expose the in-flight swap as a promise the checkout
     handlers AWAIT, and (c) queue a rapid second change so the cart always ends on the
     size last picked. giftBusy also blocks syncGift from re-adding the default in the
     gap where no gift line exists. The product-level discount keeps the line $0. */
  var giftSwapPromise = null;   // resolves when any in-flight size swap settles
  var pendingGiftVid = null;    // latest size requested while a swap is busy

  function setCheckoutButtonsBusy(on) {
    var btns = document.querySelectorAll('[data-redo-checkout], button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = !!on;
      btns[i].classList.toggle('is-busy', !!on);
    }
  }
  // Checkout handlers call this so a navigation can never outrun a size swap.
  function awaitGiftSwap() { return giftSwapPromise ? giftSwapPromise.catch(function () {}) : Promise.resolve(); }

  function performGiftSwap(lineKey, newVid) {
    giftBusy = true;
    setCheckoutButtonsBusy(true);
    giftSwapPromise = withTimeout(changeLine(lineKey, 0))
      .then(function () { return addToCart([{ id: newVid, quantity: 1, properties: { _free_gift: 'true', _size_confirmed: 'true' } }]); })
      .then(function () { return fetchCart(); })
      .then(function (fresh) {
        giftBusy = false;
        lastGiftPresent = true;
        rebuildDrawer(fresh);
        // Drain a size change requested mid-swap so the cart ends on the LAST pick.
        if (pendingGiftVid && pendingGiftVid !== newVid) {
          var nxt = pendingGiftVid; pendingGiftVid = null;
          var gl = findGiftLine(fresh);
          if (gl) return performGiftSwap(gl.key, nxt);
        }
        pendingGiftVid = null;
        setCheckoutButtonsBusy(false);
        return fresh;
      })
      .catch(function (err) {
        giftBusy = false; pendingGiftVid = null;
        setCheckoutButtonsBusy(false);
        console.warn('[cart-extras] gift size change failed', err);
        // Best-effort resync so the drawer reflects true cart state after a failure.
        return fetchCart().then(function (c) { rebuildDrawer(c); return c; }).catch(function () {});
      });
    return giftSwapPromise;
  }

  document.addEventListener('change', function (e) {
    var sel = e.target.closest && e.target.closest('[data-gift-size]');
    if (!sel) return;
    var newVid = parseInt(sel.value, 10);
    var lineKey = sel.getAttribute('data-line-key');
    if (!newVid || !lineKey) return;
    if (giftBusy) { pendingGiftVid = newVid; return; } // queue; drained after the current swap
    performGiftSwap(lineKey, newVid);
  });

  /*==========================================================================
    4. RETURN PROTECTION (Redo) — default-protected "add at checkout" flow
    Protection is NEVER a line item while browsing. The primary checkout button
    ADDS the Redo variant (qty 1, guarded against doubles) then redirects to
    /checkout — so protection is the easy default, "added at checkout". A subtle
    explainer offers a hyperlinked opt-out that strips any Redo line then checks
    out plain. On every cart render we PROACTIVELY remove any stale Redo line
    left over from the old in-cart toggle model, so the cart starts clean and
    protection is only ever added by the button click. The Redo app embed still
    owns the returns portal + order tagging.
  ==========================================================================*/
  var redoBusy = false;
  var redoCleaned = false; // one-shot guard for the stale-line cleanup
  var lastCart = null;     // most recent cart JSON seen by runAll (double-add guard)

  function redoVariantId() {
    return CFG.redo && CFG.redo.enabled && CFG.redo.variantId
      ? parseInt(CFG.redo.variantId, 10) : null;
  }

  // Find the Redo protection line in the cart (match the exact variant we add).
  function findRedoLine(cart) {
    var vid = redoVariantId();
    if (!vid || !cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === vid) return cart.items[i];
    }
    return null;
  }

  // Proactively strip any stale Redo line left from the old toggle model so the
  // cart starts clean. Runs at most once per page load and only if a line exists.
  // Removes by VARIANT id (update.js) so it doesn't depend on a fresh /cart.js read.
  function syncRedo(cart) {
    if (redoCleaned || redoBusy) return;
    var line = findRedoLine(cart);
    if (!line) { redoCleaned = true; return; }
    var vid = redoVariantId();
    redoBusy = true;
    withTimeout(updateByVariant(vid, 0))
      .then(function (fresh) { redoBusy = false; redoCleaned = true; rebuildDrawer(fresh); })
      .catch(function (e) { redoBusy = false; redoCleaned = true; console.warn('[cart-extras] stale redo cleanup failed', e); });
  }

  /* ---- Mystery-gift size gate (Adam 2026-06-29) ----
     The free Mystery Shirt is auto-added at a default variant. A shopper who never
     opens the size dropdown would otherwise check out at that default and later need
     an exchange. So: the dropdown defaults to "Select size" (no `_size_confirmed`
     property), and EVERY checkout path is blocked until a size is actively chosen
     (which sets `_size_confirmed: 'true'` via performGiftSwap). */
  function giftNeedsSize(cart) {
    var gl = cart ? findGiftLine(cart) : null;
    if (!gl) return false;
    var p = gl.properties || {};
    return p._size_confirmed !== 'true';
  }
  function promptGiftSize() {
    var sel = document.querySelector('[data-gift-size]');
    if (sel) {
      var box = (sel.closest && sel.closest('.cart__item--gift-size')) || sel.parentNode;
      if (box && box.classList) box.classList.add('cart__gift-size--error');
      var msg = box && box.parentNode ? box.parentNode.querySelector('[data-gift-size-error]') : null;
      if (!msg) msg = document.querySelector('[data-gift-size-error]');
      if (msg) msg.hidden = false;
      if (!document.documentElement.classList.contains('js-drawer-open')) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
      try { sel.scrollIntoView({ behavior: 'smooth', block: 'center' }); sel.focus(); } catch (e) {}
    } else {
      alert('Please choose a size for your free Mystery Shirt before checking out.');
    }
  }

  function goToCheckout() { window.location.href = '/checkout'; }

  /* Rob 10-22: never sell the protection on a cart Redo won't cover. Team-store gear (vendor "TS - ") is final
     sale and Redo excludes it (TS - collections + NORETURN), as it does gift cards. Coverable = any line that
     isn't team gear, a gift card, the Redo line itself or the free gift. Mirrors the Liquid first paint in
     cart-drawer.liquid. Unknown cart (failed read) -> keep the default protected flow. */
  function isTeamLine(it) { return !!(it && it.vendor && String(it.vendor).indexOf('TS - ') === 0); }
  function redoCoverable(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return true;
    var vid = redoVariantId();
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var gift = it.properties && (it.properties._free_gift === 'true' || it.properties._free_gift === true);
      if (it.variant_id === vid || it.gift_card || gift || isTeamLine(it)) continue;
      return true;
    }
    return false;
  }
  function updateRedoUI(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var cover = redoCoverable(cart);
    var team = cart.items.some(isTeamLine);
    var btns = document.querySelectorAll('[data-redo-checkout]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var label = b.getAttribute(cover ? 'data-label-protected' : 'data-label-plain');
      if (label && b.textContent.trim() !== label) b.textContent = label;
      b.classList.toggle('cart__checkout--protected', cover);
    }
    var ex = document.querySelectorAll('.cart__redo-explain');
    for (var j = 0; j < ex.length; j++) {
      ex[j].hidden = ex[j].hasAttribute('data-team-final-sale') ? !team : !cover;
    }
  }

  function setCheckoutBusy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-busy', !!on);
    btn.disabled = !!on;
  }

  // Primary button: add Redo (only if not already present) THEN go to /checkout.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-redo-checkout]');
    if (!btn) return;
    e.preventDefault();
    if (redoBusy) return;
    if (giftNeedsSize(lastCart)) { promptGiftSize(); return; } // block until Mystery Shirt size chosen

    // Honour the terms checkbox the same way the theme's checkout button would.
    if (btn.getAttribute('data-terms-required') === 'true') {
      var terms = document.querySelector('#CartTermsDrawer');
      if (terms && !terms.checked) {
        alert((window.theme && theme.strings && theme.strings.cartTermsConfirmation) ||
          'You must agree with the terms and conditions of sales to check out.');
        return;
      }
    }

    var vid = redoVariantId();
    if (!vid) { goToCheckout(); return; } // Redo unavailable -> plain checkout
    if (lastCart && !redoCoverable(lastCart)) {
      // Nothing Redo covers (e.g. team-store gear only): strip any Redo line and go to checkout plain.
      redoBusy = true;
      setCheckoutBusy(btn, true);
      withTimeout(updateByVariant(vid, 0)).then(goToCheckout, goToCheckout);
      return;
    }

    redoBusy = true;
    setCheckoutBusy(btn, true);
    // Double-add guard WITHOUT a fragile pre-fetch: rely on the last cart JSON
    // runAll already cached. Stale Redo lines are stripped on render (syncRedo) and
    // redoBusy/button-disable block rapid double-clicks, so at click time there is
    // normally no Redo line. If one somehow exists, skip the add. A direct add is
    // used (no extra /cart.js round-trip) because the storefront web-pixel can make
    // an inline GET /cart.js reject, which previously sent us down the catch path
    // and skipped the add entirely.
    // Wait out any in-flight gift size swap FIRST, so the shopper can never reach
    // checkout with the previous (e.g. default YS) Mystery Shirt size still applied.
    awaitGiftSwap().then(function () {
      if (lastCart && findRedoLine(lastCart)) return null; // already protected
      return withTimeout(addToCart([{ id: vid, quantity: 1, properties: { _redo_protection: 'true' } }]));
    }).then(function () {
      goToCheckout();
    }).catch(function (err) {
      // On failure, don't trap the shopper — proceed to checkout anyway.
      console.warn('[cart-extras] redo add-at-checkout failed', err);
      goToCheckout();
    });
  });

  // Opt-out link: strip any Redo line, then go to /checkout WITHOUT protection.
  // Removes by VARIANT id (update.js) unconditionally — a no-op server-side if the
  // line isn't present — so it works without a prior /cart.js read (which the
  // storefront web-pixel can transiently block).
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-redo-skip]');
    if (!link) return;
    e.preventDefault();
    if (redoBusy) return;
    if (giftNeedsSize(lastCart)) { promptGiftSize(); return; } // block until Mystery Shirt size chosen
    redoBusy = true;
    // Same gift-swap guard as the protected checkout path.
    awaitGiftSwap().then(function () {
      var vid = redoVariantId();
      return vid ? withTimeout(updateByVariant(vid, 0)) : Promise.resolve(null);
    }).then(function () {
      goToCheckout();
    }).catch(function (err) {
      console.warn('[cart-extras] redo opt-out failed', err);
      goToCheckout();
    });
  });

  // Standard checkout button (drawer fallback when Redo is unavailable, + the main
  // cart page). Capture phase so we intercept before the cart form submits.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[name="checkout"]');
    if (!btn) return;
    if (giftNeedsSize(lastCart)) { e.preventDefault(); e.stopPropagation(); promptGiftSize(); }
  }, true);

  /*==========================================================================
    ORCHESTRATION
  ==========================================================================*/
  var syncing = false;
  function runAll(cart) {
    // Guard against a malformed / non-JSON /cart.js response (maintenance page,
    // password page, 429) so one bad payload can't throw and kill every add-on.
    if (!cart || !Array.isArray(cart.items) || typeof cart.total_price !== 'number') return;
    lastCart = cart; // cache for the checkout-button double-add / opt-out guards
    updateRedoUI(cart);
    updateShippingBar(cart);
    loadUpsell(cart);
    syncRedo(cart);
    if (syncing) return;            // avoid gift re-entrancy across the rebuild it triggers
    syncing = true;
    // Master switch OFF, or a non-USD market (tiers are USD cents; a CAD cart would be compared
    // 1:1 and unlock free shipping at CA$75 and auto-add a gift Canada pays for) -> strip any
    // auto-added free lines, hide the bar, and do nothing else. (09-30 canada-fix, rebased 10-02.)
    var nonUsd = cart.currency && cart.currency !== 'USD';
    if (nonUsd) {
      var bars = document.querySelectorAll('[data-tier-bar]');
      for (var bi = 0; bi < bars.length; bi++) bars[bi].style.display = 'none';
    }
    if ((CFG && CFG.enabled === false) || nonUsd) {
      removeAllFreeLines(cart).then(function () { syncing = false; }, function () { syncing = false; });
      return;
    }
    // Primary product reward first (size-swap machinery), then any extra product tiers.
    syncGift(cart)
      .then(function (c) { return syncRewardExtras(c || cart); })
      .then(function () { syncing = false; }, function () { syncing = false; });
  }

  function onCartError(e) { console.warn('[cart-extras] cart fetch failed', e); }

  // Freshness: several listeners refetch at once, so a slower OLDER /cart.js answer
  // must never repaint over a newer one. Stale answers resolve to null (runAll ignores).
  var cartSeq = 0, appliedSeq = 0;
  function freshCart() {
    var mine = ++cartSeq;
    return fetchCart().then(function (c) {
      if (mine < appliedSeq) return null;
      appliedSeq = mine;
      return c;
    });
  }

  // qty changes carry full cart JSON
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) { appliedSeq = ++cartSeq; runAll(e.detail.cart); }
  });

  // add-to-cart events do NOT carry cart JSON -> refetch
  document.addEventListener('ajaxProduct:added', function () {
    freshCart().then(runAll).catch(onCartError);
  });

  /* Hobolax 09-30 (Adam: bundle read "$6 to go", tapping a drawer upsell tee jumped it to
     "$75.05 away", drawer then showed the tee alone). The open drawer can show a cart that
     no longer exists: Easy Bundle removes lines / wipes the cart cookie with NO theme event
     (its refresh helper only knows Rebuy-style drawers), another tab edits the cart, or a
     tab left open for days is resumed. The next add then exposes the real cart. So after
     ANY script's /cart/* write, and whenever the tab is shown again or restored from
     bfcache, re-read /cart.js and repaint drawer + bar if it isn't what we last painted. */
  function cartSig(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return (c.token || '') + '|' + c.total_price + '|' +
      c.items.map(function (it) { return it.key + ':' + it.quantity; }).join(',');
  }
  var resyncTimer = null;
  function resyncIfStale(delay) {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(function () {
      freshCart().then(function (c) {
        if (!c || !Array.isArray(c.items)) return;
        if (lastCart && cartSig(c) === cartSig(lastCart)) return; // view is current
        rebuildDrawer(c); // repaints the Impulse drawer + cart:updated -> runAll
      }).catch(onCartError);
    }, delay == null ? 400 : delay);
  }
  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (en) {
        if ((en.initiatorType === 'fetch' || en.initiatorType === 'xmlhttprequest') &&
            /\/cart\/(add|change|update|clear)/.test(en.name)) resyncIfStale();
      });
    }).observe({ entryTypes: ['resource'] });
  } catch (e) { /* no PerformanceObserver: the theme/app event listeners still apply */ }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') resyncIfStale(0);
  });

  // Third-party cart writers (Easy Bundle's $99 builder) add via their own fetch
  // and repaint the drawer with a bare `cart:build` — no cart:updated, no
  // ajaxProduct:added — so the bar/gift/Redo kept the pre-add cart ("$105 away"
  // on a $99 bundle). Refetch on any cart:build we didn't fire ourselves.
  // Debounced: the app fires several refresh events in a burst.
  var ownBuild = false, foreignBuildTimer = null;
  document.addEventListener('cart:build', function () {
    if (ownBuild) return;
    clearTimeout(foreignBuildTimer);
    foreignBuildTimer = setTimeout(function () { freshCart().then(runAll).catch(onCartError); }, 150);
  });

  // initial paint. Retry the first /cart.js a few times: a storefront web-pixel
  // can transiently make an early fetch reject, and the initial paint is what
  // kicks off the proactive stale-Redo cleanup (runAll -> syncRedo), so we don't
  // want a single early rejection to skip it.
  function initialPaint(attempt) {
    freshCart().then(runAll).catch(function (e) {
      attempt = attempt || 0;
      if (attempt < 3) { setTimeout(function () { initialPaint(attempt + 1); }, 600 * (attempt + 1)); }
      else { onCartError(e); }
    });
  }
  document.addEventListener('DOMContentLoaded', function () {
    // BLIND stale-Redo cleanup: under the new default-protected model, protection
    // is never a line while browsing — a Redo line on load can only be a leftover
    // from the old in-cart toggle model. Fire a one-time variant-targeted removal
    // that no-ops server-side if absent. This does NOT depend on a /cart.js read
    // (which a storefront web-pixel can block), so the cart reliably starts clean
    // even when the read-driven syncRedo() path can't run. Guarded so it only ever
    // runs once and never fights a deliberate add-at-checkout (which navigates away).
    var blindVid = redoVariantId();
    if (blindVid && !redoCleaned && !redoBusy) {
      redoBusy = true;
      withTimeout(updateByVariant(blindVid, 0))
        .then(function (fresh) { redoBusy = false; redoCleaned = true; if (fresh && Array.isArray(fresh.items)) rebuildDrawer(fresh); })
        .catch(function () { redoBusy = false; /* leave redoCleaned false so read-path can retry */ });
    }
    initialPaint(0);
  });

  // bfcache restore guard (esp. mobile back from checkout). A normal navigation
  // re-runs this whole script with fresh state, but a back-forward-cache restore
  // brings the page back with our in-memory flags FROZEN — so redoBusy can still be
  // true and the checkout button left disabled, leaving it permanently unclickable.
  // On a persisted restore, reset the transient busy flags and re-enable the buttons.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    redoBusy = false; giftBusy = false; syncing = false;
    giftSwapPromise = null; pendingGiftVid = null;
    var btns = document.querySelectorAll('[data-redo-checkout], button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = false;
      btns[i].classList.remove('is-busy', 'is-loading');
    }
    resyncIfStale(0); // the restored drawer is a snapshot — the cart may have moved on
  });
})();
